import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  Mail,
  Phone,
  Building2,
  LayoutGrid,
  Camera,
  Save,
  ArrowRight,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "../../store/slices/authSlice";
import styles from "./ProfilePage.module.css";

export default function ProfilePage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.startsWith("ar");

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    field: "",
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const res = await api.get("/profile");
        if (res.data.code === 200) {
          const data = res.data.data;
          setProfile(data);
          setForm({
            name: data.name || "",
            email: data.email || "",
            phone: data.phone || "",
            company_name: data.company_name || "",
            field: data.field || "",
          });
          if (data.image) setImagePreview(data.image);
        }
      } catch (err) {
        console.error("Profile fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccessMsg("");
    setErrorMsg("");
    try {
      const formData = new FormData();
      formData.append("name", form.name);
      formData.append("phone", form.phone);
      if (form.company_name) formData.append("company_name", form.company_name);
      if (form.field) formData.append("field", form.field);
      if (imageFile) formData.append("image", imageFile);

      const res = await api.post("/updateProfile", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.code === 200) {
        setSuccessMsg(t("profile.success"));
        setTimeout(() => setSuccessMsg(""), 3000);
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.message || t("profile.error"));
      setTimeout(() => setErrorMsg(""), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingWrap}>
        <div className={styles.spinnerRing} />
      </div>
    );
  }

  return (
    <div className={styles.page} dir={isRtl ? "rtl" : "ltr"}>
      <div className={styles.pageHeader}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          <ArrowRight size={18} />
        </button>
        <h1 className={styles.pageTitle}>{t("profile.title")}</h1>
      </div>

      <div className={styles.card}>
        <div className={styles.avatarSection}>
          <div className={styles.avatarWrap}>
            {imagePreview ? (
              <img
                src={imagePreview}
                alt="Avatar"
                className={styles.avatarImg}
              />
            ) : (
              <div className={styles.avatarPlaceholder}>
                <User size={40} strokeWidth={1.2} color="#9ca3af" />
              </div>
            )}
            <button
              className={styles.cameraBtn}
              onClick={() => fileInputRef.current?.click()}
              title={t("profile.changePhoto")}
            >
              <Camera size={16} color="#fff" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleImageChange}
            />
          </div>
        </div>

        <div className={styles.formSection}>
          <Field
            label={t("profile.fullName")}
            icon={<User size={18} color="#0c6839" />}
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder={t("profile.fullName")}
          />

          <Field
            label={t("profile.email")}
            icon={<Mail size={18} color="#0c6839" />}
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder={t("profile.email")}
            disabled
          />

          <Field
            label={t("profile.phone")}
            icon={<Phone size={18} color="#0c6839" />}
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder={t("profile.phone")}
          />

          <Field
            label={t("profile.companyName")}
            icon={<Building2 size={18} color="#0c6839" />}
            name="company_name"
            value={form.company_name}
            onChange={handleChange}
            placeholder={t("profile.companyName")}
          />

          <Field
            label={t("profile.field")}
            icon={<LayoutGrid size={18} color="#0c6839" />}
            name="field"
            value={form.field}
            onChange={handleChange}
            placeholder={t("profile.field")}
          />

          {successMsg && <p className={styles.successMsg}>{successMsg}</p>}
          {errorMsg && <p className={styles.errorMsg}>{errorMsg}</p>}

          <button
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <span className={styles.savingDots}>{t("profile.saving")}</span>
            ) : (
              <>
                <Save size={16} />
                {t("profile.save")}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, icon, name, value, onChange, placeholder, disabled }) {
  return (
    <div className={styles.field}>
      <label className={styles.label}>{label}</label>
      <div
        className={`${styles.inputWrap} ${disabled ? styles.inputDisabled : ""}`}
      >
        <span className={styles.inputIcon}>{icon}</span>
        <input
          type="text"
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          className={styles.input}
        />
      </div>
    </div>
  );
}
